/**
 * Local compile bridge client. The service wraps arduino-cli; when it is not
 * running the UI falls back to downloading the generated sketch so the user
 * can flash it through the Arduino IDE or arduino-cli themselves.
 */

export interface CompileImage {
  name: string;
  offset: number;
  dataBase64: string;
}

export interface CompileResult {
  ok: boolean;
  log: string;
  images: CompileImage[];
  /** Libraries the bridge newly installed before compiling (may be empty). */
  installedLibraries?: string[];
}

export interface CompileOptions {
  compileUrl: string;
  fqbn: string;
  libraries: string[];
}

export async function compileSketch(sketch: string, options: CompileOptions): Promise<CompileResult> {
  const url = `${options.compileUrl.replace(/\/$/, '')}/api/compile`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sketch,
        fqbn: options.fqbn,
        libraries: options.libraries,
      }),
    });
  } catch {
    throw new Error(
      `The compile service is not reachable at ${options.compileUrl}. Start it with "npm run dev:service", or download the sketch below.`,
    );
  }

  const data = (await res.json().catch(() => null)) as (CompileResult & { error?: string }) | null;
  if (!data) {
    throw new Error(`The compile service returned an unreadable response (HTTP ${res.status}).`);
  }
  return {
    ok: Boolean(data.ok),
    log: data.log ?? '',
    images: data.images ?? [],
    installedLibraries: data.installedLibraries ?? [],
  };
}

export interface CompileHealth {
  ok: boolean;
  arduinoCli: string | null;
}

export async function checkServiceHealth(compileUrl: string): Promise<CompileHealth> {
  try {
    const res = await fetch(`${compileUrl.replace(/\/$/, '')}/api/health`);
    if (!res.ok) return { ok: false, arduinoCli: null };
    return (await res.json()) as CompileHealth;
  } catch {
    return { ok: false, arduinoCli: null };
  }
}

export function downloadSketch(sketch: string, projectName: string): void {
  const blob = new Blob([sketch], { type: 'text/x-arduino' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${projectName || 'sketch'}.ino`;
  a.click();
  URL.revokeObjectURL(url);
}
